import { put, take, call, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  eventDetailsSelectors,
  getEventDetailsSuccess,
  getEventDetailsError,
  extendEventDetails,
  updateButtonState,
  getEventDetailsForEntityNew,
  setLoadingEvents,
  setLoadingMoreEvents,
} from '../../../reducers/eventDetailsSlice';

import { api } from '../../../api/lelyBackend/eventsPageApi';
import { EventsButtonType } from '../types';
import {
  AwaitedApiResultType,
  Nullable,
  EventsSearchObject,
} from '../../../common/types';
import withLoading from '../../../sagas/loadingSaga';

export function* eventPageDetailsSaga() {
  while (true) {
    const eventDetailsAction = yield take(getEventDetailsForEntityNew.type);
    yield* withLoading(function* () {
      const offset = yield select(eventDetailsSelectors.latestOffset);
      const searchId = yield select(eventDetailsSelectors.searchId);
      const latestAppliedFilters = yield select(
        eventDetailsSelectors.latestAppliedFilters,
      );

      yield put(setLoadingEvents(true));
      const {
        entityId,
        startDateToTimestamp,
        endDateToTimestamp,
        limit,
        page,
      } = eventDetailsAction.payload;

      yield call(
        getEventDetails,
        entityId,
        startDateToTimestamp,
        endDateToTimestamp,
        latestAppliedFilters.appliedTypes,
        latestAppliedFilters.appliedSearchText,
        page,
        offset,
        limit,
        searchId,
      );
    });
  }
}

export function* getEventDetails(
  entityId: string,
  startDateToTimestamp: string | undefined,
  endDateToTimestamp: string | undefined,
  namesOfSelectedTypes: string[],
  searchObject: EventsSearchObject,
  page: Nullable<string>,
  offset: number,
  limit: number,
  searchId,
) {
  try {
    const eventsForEntityData: AwaitedApiResultType<
      typeof api.getEventsForEntity
    > = yield call(
      api.getEventsForEntity,
      entityId,
      startDateToTimestamp,
      endDateToTimestamp,
      namesOfSelectedTypes,
      searchObject,
      page,
      offset,
      limit,
      searchId,
    );
    const eventsForEntity = eventsForEntityData.data;
    yield put(getEventDetailsSuccess(eventsForEntity));
    yield put(setLoadingEvents(false));
  } catch (error: any) {
    yield put(setLoadingEvents(false));
    yield put(getEventDetailsError(error));
  }
}

export function* getMoreEvents(
  action: PayloadAction<{
    entityId: string;
    type: EventsButtonType;
    limit?: number;
    id?: string;
  }>,
) {
  const { entityId, type, limit, id } = action.payload;
  yield put(setLoadingMoreEvents({ isLoading: true, type }));

  // TODO: Fix appliedSearchText's type up the chain
  const { appliedTypes, appliedSearchText } = yield select(
    eventDetailsSelectors.latestAppliedFilters,
  );

  yield call(
    getMoreEventsDetails,
    entityId,
    appliedTypes,
    appliedSearchText as EventsSearchObject,
    type,
    id,
    limit,
  );
}

export function* getMoreEventsDetails(
  entityId: string,
  namesOfSelectedTypes: string[],
  searchObject: EventsSearchObject,
  type: EventsButtonType,
  id?: string,
  limit = 10,
) {
  try {
    const eventsForEntityData: AwaitedApiResultType<typeof api.getMoreEvents> =
      yield call(
        api.getMoreEvents,
        entityId,
        namesOfSelectedTypes,
        searchObject,
        limit,
        type,
        id,
      );
    const events = eventsForEntityData.data;
    yield put(
      extendEventDetails({
        type,
        events,
        item: id || null,
      }),
    );
    yield put(
      updateButtonState({
        eventsLength: events.length,
        type,
        offset: limit,
      }),
    );
    yield put(setLoadingMoreEvents({ isLoading: false, type }));
  } catch (error: any) {
    yield put(getEventDetailsError(error));
    yield put(setLoadingMoreEvents({ isLoading: false, type }));
  }
}
